'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import { LayoutDashboard, Map, BookOpen, Trophy, ShieldCheck, ChevronLeft, ChevronRight, Settings, LogOut } from 'lucide-react';
import { useLanguage } from '@/lib/LanguageContext';
import { useApp } from '@/lib/AppContext';
import { t } from '@/lib/i18n';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const navItems = [
  { href: '/', icon: LayoutDashboard, labelKey: 'nav_dashboard' as const, badge: null },
  { href: '/learning-path-screen', icon: Map, labelKey: 'nav_learning' as const, badge: null },
  { href: '/lesson-screen', icon: BookOpen, labelKey: 'nav_lesson' as const, badge: null },
  { href: '/leaderboard', icon: Trophy, labelKey: 'nav_leaderboard' as const, badge: null },
  { href: '/admin-panel', icon: ShieldCheck, labelKey: 'nav_admin' as const, badge: null },
];

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();
  const { language } = useLanguage();
  const { user, logout } = useApp();
  const router = useRouter();

  const handleLogout = () => {
    logout();
    toast.success(language === 'fr' ? 'Déconnexion réussie' : 'Выход выполнен');
    router.push('/sign-up-login-screen');
  };

  return (
    <aside
      className={`relative flex flex-col bg-card border-r border-border transition-all duration-300 ease-in-out ${
        collapsed ? 'w-16' : 'w-60'
      } flex-shrink-0`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-border ${collapsed ? 'justify-center' : ''}`}>
        <AppImage
          src="/assets/images/1779969643398-1780552779266.png"
          alt="Vika Comptable logo"
          width={36}
          height={36}
          className="rounded-lg flex-shrink-0"
        />
        {!collapsed && (
          <span className="font-bold text-base text-foreground tracking-tight">
            Vika Comptable
          </span>
        )}
      </div>

      {/* Toggle button */}
      <button
        onClick={onToggle}
        className="absolute -right-3 top-16 z-10 w-6 h-6 bg-primary rounded-full flex items-center justify-center text-white shadow-md hover:bg-primary/90 transition-colors"
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
      </button>

      {/* Nav */}
      <nav className="flex-1 px-2 py-4 space-y-1">
        {!collapsed && (
          <p className="text-xs font-600 uppercase tracking-widest text-muted-foreground mb-3 px-2">
            Menu
          </p>
        )}
        {navItems.map((item) => {
          const isActive = pathname === item.href || (item.href !== '/' && pathname.startsWith(item.href));
          const Icon = item.icon;
          return (
            <Link
              key={`nav-${item.href}`}
              href={item.href}
              title={t(language, item.labelKey)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-150 group relative ${
                isActive
                  ? 'bg-primary/10 text-primary font-600' :'text-muted-foreground hover:bg-secondary hover:text-foreground'
              }`}
            >
              <Icon size={20} className="flex-shrink-0" />
              {!collapsed && (
                <span className="text-sm font-500 truncate">{t(language, item.labelKey)}</span>
              )}
              {collapsed && (
                <span className="absolute left-full ml-3 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap transition-opacity z-50">
                  {t(language, item.labelKey)}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-border p-3 space-y-1">
        <button
          onClick={() => toast.info(language === 'fr' ? 'Paramètres — bientôt disponible' : 'Настройки — скоро')}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-muted-foreground hover:bg-secondary hover:text-foreground transition-all duration-150 group relative ${collapsed ? 'justify-center' : ''}`}
          title={t(language, 'nav_settings')}
        >
          <Settings size={18} className="flex-shrink-0" />
          {!collapsed && <span className="text-sm">{t(language, 'nav_settings')}</span>}
          {collapsed && (
            <span className="absolute left-full ml-3 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
              {t(language, 'nav_settings')}
            </span>
          )}
        </button>

        <button
          onClick={handleLogout}
          className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl text-danger hover:bg-danger/10 transition-all duration-150 group relative ${collapsed ? 'justify-center' : ''}`}
          title={t(language, 'nav_logout')}
        >
          <LogOut size={18} className="flex-shrink-0" />
          {!collapsed && <span className="text-sm">{t(language, 'nav_logout')}</span>}
          {collapsed && (
            <span className="absolute left-full ml-3 px-2 py-1 bg-foreground text-background text-xs rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap z-50">
              {t(language, 'nav_logout')}
            </span>
          )}
        </button>

        <div className={`flex items-center gap-3 px-3 py-2 rounded-xl ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center text-white text-xs font-700 flex-shrink-0">
            {user?.avatar}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-600 text-foreground truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.rank}</p>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}